import React, { useState, useRef, useEffect } from "react"
import { Sun, Moon, Zap, ChevronDown } from "lucide-react"
import { useTheme } from "./ThemeProvider"
import { useLang } from "./i18n/LangContext"
import { useWaitlistModal } from "./WaitlistModalContext"

const LANGS = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "bn", label: "বাংলা" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Español" },
]

function LangSwitcher() {
  const { lang, setLang, t } = useLang()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
        aria-label="Switch language"
      >
        {t.lang}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-36 rounded-xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 py-1 z-50">
          {LANGS.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => { setLang(code); setOpen(false) }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                lang === code
                  ? "text-brand-600 dark:text-brand-400 font-medium bg-brand-50 dark:bg-brand-950"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function SiteNav({ logoUrl }) {
  const { theme, toggle } = useTheme()
  const { t } = useLang()
  const { openModal } = useWaitlistModal()

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm">
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 flex h-16 items-center justify-between">
        <a href="/" className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white" aria-label="Appdesh">
          {logoUrl ? (
            <img src={logoUrl} alt="Appdesh" className="h-15 w-auto" />
          ) : (
            <>
              <Zap className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              Appdesh
            </>
          )}
        </a>

        <div className="flex items-center gap-2">
          <a
            href="/support"
            className="block rounded-md px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
          >
            {t.nav.support}
          </a>
          <a
            href="/partner_home"
            className="block rounded-md px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
          >
            {t.nav.dashboard}
          </a>

          <LangSwitcher />

          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="rounded-md p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <button
            type="button"
            onClick={() => openModal("nav")}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 transition-colors"
          >
            {t.nav.joinWaitlist}
          </button>
        </div>
      </nav>
    </header>
  )
}
